import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLang } from "../../contexts/LanguageContext";
import './Transcription.css';

const convertirAEnlacesJSX = (lemmatizationArray, phonologizationArray, handleWordClick, currentWordId) => {
  let currentParagraph = [];
  let paragraphType = null;

  const elements = [];

  lemmatizationArray.forEach((item, i) => {
    if (Array.isArray(item)) {
      const [word, lemma, pos, metadata = {}] = item;
      const phonology = phonologizationArray[i] && phonologizationArray[i][0] ? phonologizationArray[i][0] : '';

      // --- Gestión de Párrafos RSM (Apertura) ---
      const supportedTags = ['t', 's', 'r', 'v', 'pst', 'pr']; 
      if (metadata.tag && !metadata.end_tag && supportedTags.includes(metadata.tag)) {
        const targetType = metadata.tag === 't' ? 'title' : 
                          (metadata.tag === 's' || metadata.tag === 'pst') ? 'subtitle' : 
                          (metadata.tag === 'r' || metadata.tag === 'pr') ? 'resume' : 
                          metadata.tag === 'v' ? 'stanza' : 'paragraph';

        if (paragraphType !== targetType) {
          // Si hay contenido previo y el tipo cambia, cerramos el párrafo actual
          if (currentParagraph.length > 0) {
            elements.push(
              <p className={paragraphType || 'paragraph'} key={`p-${i}`}>
                {currentParagraph}
              </p>
            );
            currentParagraph = [];
          }
          // Asignar el nuevo tipo de párrafo
          paragraphType = targetType;
        }
      }

      if (pos === 'space') {
        if (word.includes('\n') && !metadata.tag) {
          // Salto de línea manual si no estamos dentro de una etiqueta RSM
          currentParagraph.push(<br key={`br-${i}`} />);
        } else {
          currentParagraph.push(word);
        }

        // Lógica de respaldo para párrafos tradicionales (doble salto)
        if (word === '\n\n' || word === '\r\n\r\n') {
          if (currentParagraph.length > 0) {
            elements.push(
              <p className={paragraphType || 'paragraph'} key={`p-break-${i}`}>
                {currentParagraph}
              </p>
            );
            currentParagraph = [];
            paragraphType = null;
          }
        }
      } else if (pos === 'punct') {
        currentParagraph.push(word);
      } else {
        // --- Uso de SYNC_ID centralizado ---
        const syncId = metadata && metadata.sync_id ? String(metadata.sync_id) : null;
        const isCurrentWord = syncId !== null && syncId === String(currentWordId);

        const anchor = (
          <a
            className={`mostrarModal ${isCurrentWord ? 'current-word' : ''} ${metadata.lang ? `lang-${metadata.lang}` : ''}`}
            href="#"
            key={`a-${i}`}
            id={syncId || `word-${i}`}
            data-sync-id={syncId}
            onClick={(e) => {
              e.preventDefault();
              handleWordClick(lemma, pos, syncId);
            }}
          >
            {word}
            <span className="p">{pos}</span>
            <span className="fon" style={{ display: 'none' }}>{phonology}</span>
          </a>
        );
        currentParagraph.push(anchor);
      }

      // --- Gestión de Párrafos RSM (Cierre) ---
      if (metadata.end_tag && supportedTags.includes(metadata.tag)) {
        elements.push(
          <p className={paragraphType || 'paragraph'} key={`p-end-${i}`}>
            {currentParagraph}
          </p>
        );
        currentParagraph = [];
        paragraphType = null;
      }
    } else {
      currentParagraph.push(item);
    }
  });

  // Párrafo final si queda contenido
  if (currentParagraph.length > 0) {
    elements.push(
      <p className={paragraphType || 'paragraph'} key="p-last">
        {currentParagraph}
      </p>
    );
  }

  return elements;
};

// Transcription component
const Transcription = ({ lemmatizationArray, phonologizationArray, handleWordClick, currentWordId, fontSize, showLemma }) => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const autoScrollRef = useRef(true);
  const suppressNextScrollRef = useRef(false);
  const suppressTimeoutRef = useRef(null);
  const lastLineTopRef = useRef(null);

  useEffect(() => {
    if (lemmatizationArray && lemmatizationArray.length > 0) {
      const firstToken = lemmatizationArray[0];
      const version = firstToken[3] && firstToken[3].v ? firstToken[3].v : "DESCONOCIDA (Vieja)";
      
      // Verificar si hay algún sync_id en el array
      const hasSyncId = lemmatizationArray.some(item => Array.isArray(item) && item[3] && item[3].sync_id);
      if (!hasSyncId) {
        console.warn("[Readerum] Advertencia: Este capítulo no tiene IDs de sincronización RSM 2.0.");
      }
    }
  }, [lemmatizationArray]);

  useEffect(() => {
    if (currentWordId && currentWordId !== -1) {
      // Sincronía activa (sin logs ruidosos en producción)
    }
  }, [currentWordId, lemmatizationArray]);

  useEffect(() => {
    autoScrollRef.current = autoScrollEnabled;
    if (!autoScrollEnabled) {
      lastLineTopRef.current = null;
    }
  }, [autoScrollEnabled]);

  useEffect(() => {
    const transcription = document.getElementById("transcription");
    const sizeClasses = ["uTextA", "uTextB", "uTextC", "uTextD", "uTextE"];
    transcription.classList.remove(...sizeClasses);
    transcription.classList.add(sizeClasses[fontSize]);
  }, [fontSize]);

  useEffect(() => {
    const phonologySpans = document.querySelectorAll(".fon");
    phonologySpans.forEach(span => {
      span.style.display = showLemma ? 'inline' : 'none';
    });
  }, [showLemma]);

  useEffect(() => {
    if (!autoScrollRef.current) return;
    if (!currentWordId) return;
    const currentAnchor = document.getElementById(String(currentWordId));
    if (!currentAnchor) return;

    const anchorRect = currentAnchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const computedStyles = window.getComputedStyle(currentAnchor);
    const parsedLineHeight = parseFloat(computedStyles.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
      ? parsedLineHeight
      : Math.max(anchorRect.height || 0, 20);
    const lastLineTop = lastLineTopRef.current;

    const lineThreshold = lineHeight * 0.75;
    const lineChanged = lastLineTop !== null && Math.abs(anchorRect.top - lastLineTop) >= lineThreshold;

    const ensureVisible = () => {
      const topThreshold = 80;
      const bottomThreshold = 80;
      const needsScroll =
        anchorRect.top < topThreshold || anchorRect.bottom > viewportHeight - bottomThreshold;

      if (needsScroll) {
        suppressNextScrollRef.current = true;
        if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
        const target = window.scrollY + anchorRect.top - (viewportHeight / 2);
        window.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
        suppressTimeoutRef.current = setTimeout(() => {
          suppressNextScrollRef.current = false;
        }, 350);
        return viewportHeight / 2;
      }
      return anchorRect.top;
    };

    if (lineChanged) {
      const direction = anchorRect.top > lastLineTop ? 1 : -1;
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      window.scrollBy({ top: direction * lineHeight, behavior: 'smooth' });
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 250);
      lastLineTopRef.current = Math.round(anchorRect.top - direction * lineHeight);
      return;
    }

    const updatedTop = ensureVisible();

    lastLineTopRef.current = Math.round(updatedTop);
  }, [currentWordId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleUserScroll = (event) => {
      if (!event) return;
      if (!event.isTrusted) return; // ignora scroll programático
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleWheel = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    const handleTouchMove = () => {
      if (suppressNextScrollRef.current) return;
      if (!autoScrollRef.current) return;
      setAutoScrollEnabled(false);
      lastLineTopRef.current = null;
    };

    container.addEventListener('scroll', handleUserScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('scroll', handleUserScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleUserScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', handleUserScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (suppressTimeoutRef.current) {
        clearTimeout(suppressTimeoutRef.current);
      }
    };
  }, []);

  const handleEnableAutoScroll = useCallback(() => {
    lastLineTopRef.current = null;
    setAutoScrollEnabled(true);
    const currentAnchor = currentWordId ? document.getElementById(String(currentWordId)) : null;
    if (currentAnchor) {
      suppressNextScrollRef.current = true;
      if (suppressTimeoutRef.current) clearTimeout(suppressTimeoutRef.current);
      currentAnchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      suppressTimeoutRef.current = setTimeout(() => {
        suppressNextScrollRef.current = false;
      }, 400);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      lastLineTopRef.current = Math.round(viewportHeight / 2);
    }
  }, [currentWordId]);

  const transcriptionContent = convertirAEnlacesJSX(
    lemmatizationArray,
    phonologizationArray,
    handleWordClick,
    currentWordId
  );

  return (
    <div id="transcription" ref={containerRef}>
      {transcriptionContent}
      {!autoScrollEnabled && (
        <div className="autoscroll-toggle">
          <button type="button" className="autoscroll-button" onClick={handleEnableAutoScroll}>
            {t('CHAPTER_AUTOSCROLL_RESUME')}
          </button>
        </div>
      )}
    </div>
  );
};

export default Transcription;
